/** @typedef {import('./index').TabsViewProps} TabsViewProps */

export const ComponentStateMode = {
  Controlled: 0,
  Uncontrolled: 1
};

export const getTabKey = tab => String(tab.id ?? tab.label);

export const a11yProps = ({ id, index, label }) => {
  const tabId = `tab-${id || index}`;
  return {
    id: tabId,
    'aria-controls': tabId,
    'data-testid': tabId,
    'aria-label': label
  };
};

/**
 * @param {TabsViewProps} props
 */
export const getModeFromProps = props => {
  const isControlled = props.selectedTab !== undefined && props.onChangeTabIndex !== undefined;
  const isUncontrolled = props.initialTab !== undefined;

  if (isControlled && isUncontrolled)
    throw new Error(
      'TabsView cannot operate in Controlled and Uncontrolled mode at the same time.'
    );

  return isControlled ? ComponentStateMode.Controlled : ComponentStateMode.Uncontrolled;
};

/**
 * @param {TabsViewProps} props
 */
export const checkForIllegalModeChange = (props, mode) => {
  if (process.env.NODE_ENV === 'production') return;

  if (mode === ComponentStateMode.Controlled && props.enableUrlNavigation) {
    throw new Error('URL navigation in `TabsView` could only be used in uncontrolled mode');
  }

  if (mode !== undefined && mode !== getModeFromProps(props)) {
    throw new Error(
      `Switching between controlled and uncontrolled mode is not supported in \`TabsView\`. For controlled mode, you need to pass \`selectedTab\` and \`onChangeTabIndex\` from parent component. 
       For uncontrolled mode, you can pass only \`initialTab\`.`
    );
  }
};

export const orderTabs = ({ tabs, orderedTabIds }) => {
  const tabsById = Object.fromEntries(tabs.map(tab => [getTabKey(tab), tab]));
  const orderedTabs = orderedTabIds.map(id => tabsById[String(id)]).filter(Boolean);
  const orderedIds = new Set(orderedTabs.map(getTabKey));

  return orderedTabs.concat(tabs.filter(tab => !orderedIds.has(getTabKey(tab))));
};

export const buildInitialOrder = ({ organizationStorageKey, tabs }) => {
  try {
    if (!organizationStorageKey) return tabs;

    const saved =
      typeof window !== 'undefined' ? window.localStorage.getItem(organizationStorageKey) : null;
    if (!saved) return tabs;

    const savedIds = JSON.parse(saved);
    if (!Array.isArray(savedIds)) return tabs;

    return orderTabs({ tabs, orderedTabIds: savedIds });
  } catch (e) {
    return tabs;
  }
};

export const mergeVisibleTabOrder = ({ orderedTabs, visibleTabs }) => {
  let visibleIndex = 0;

  return orderedTabs.map(tab => {
    if (tab.visible === false) return tab;

    const reorderedTab = visibleTabs[visibleIndex];
    visibleIndex += 1;
    return reorderedTab || tab;
  });
};
